import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { BookOpenCheck } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import API_ENDPOINTS from '../../config/api';

/**
 * Peilaus — what the knowledge base says about your pain point
 * (specs/impact-path-v2-prd.md §4i, second pre-task).
 *
 * For a finished Kipupiste, the backend retrieves passages for everything
 * that was kept and returns at most three mirrors: one thing you said, one
 * thing a source says, and the question whether it holds where you work.
 * Each stance becomes a note on the node, with the source. Disagreement is
 * welcome; it is what the room talks about first.
 */

const Card = styled.div`
  background: #141821;
  border: 1px solid #232a36;
  border-radius: 10px;
  padding: 1.25rem 1.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const Muted = styled.p`
  margin: 0;
  color: #98a2b3;
  line-height: 1.5;
`;

const Lens = styled.span`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #7b8794;
`;

const Mirror = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  border: 1px solid #232a36;
  background: #181d28;
  opacity: ${(p) => (p.$decided ? 0.85 : 1)};
`;

const You = styled.div`
  border-left: 3px solid #5B9DD9;
  padding-left: 0.8rem;
  color: #d5dbe5;
`;

const Source = styled.div`
  border-left: 3px solid #f2c14e;
  padding-left: 0.8rem;
`;

const Cite = styled.div`
  font-size: 0.82rem;
  color: #98a2b3;
  margin-top: 0.25rem;
`;

const Question = styled.div`
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Stance = styled.button`
  all: unset;
  cursor: pointer;
  padding: 0.45rem 0.8rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  border: 1px solid ${(p) => (p.$tone === 'holds' ? '#2f6b45' : p.$tone === 'not_here' ? '#5a2c31' : '#2c3444')};
  background: ${(p) => (p.$tone === 'holds' ? '#1f3d2a' : p.$tone === 'not_here' ? '#2a1f22' : 'transparent')};
  &:disabled { opacity: 0.5; cursor: default; }
`;

const Note = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: #0f131b;
  color: #e8ecf2;
  border: 1px solid #2c3444;
  border-radius: 8px;
  padding: 0.55rem 0.8rem;
  font: inherit;
  &:focus { outline: none; border-color: #5B9DD9; }
`;

const Decided = styled.div`
  font-size: 0.9rem;
  color: #6fd3c8;
  font-weight: 600;
`;

const Pick = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.9rem;
  border-radius: 8px;
  border: 1px solid #2c3444;
  &:hover { border-color: #3a4457; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const ErrorLine = styled.p`
  margin: 0;
  color: #FF8A85;
`;

async function call(url, body) {
  const res = await fetch(url, {
    method: body ? 'POST' : 'GET',
    credentials: 'include',
    // Ask for JSON, so an auth or server failure comes back as a message
    // rather than the backend's HTML login page and a bare "HTTP 401".
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

/**
 * @param {object} props
 * @param {string|null} [props.interviewId] mirror this one finished pain point
 *   only — the grouped card on the list shows the mirror under its pain point.
 * @param {(peilaus: object) => void} [props.onChange] every saved stance, so
 *   the card can mark the pair done.
 */
export default function Peilaus({ instanceId, task, interviewId = null, readOnly = false, onChange = null }) {
  const { t } = useTranslation();
  const [kysy, setKysy] = useState(null);
  const [mirrorsByNode, setMirrorsByNode] = useState({});
  const [current, setCurrent] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const q = `?instanceId=${encodeURIComponent(instanceId)}`;
    Promise.all([call(API_ENDPOINTS.ME_KYSY() + q), call(API_ENDPOINTS.ME_PEILAUS() + q)])
      .then(([k, p]) => {
        if (cancelled) return;
        const done = (k.interviews || []).filter((i) => i.status === 'done' && (!interviewId || i.id === interviewId));
        setKysy(done);
        const byNode = Object.fromEntries((p.mirrors || []).map((m) => [m.nodeId, m]));
        setMirrorsByNode(byNode);
        const own = interviewId ? byNode[done[0]?.nodeId] || null : null;
        const first = own || (p.mirrors || []).find((m) => m.status === 'open') || (p.mirrors || [])[0] || null;
        setCurrent(interviewId && !own ? null : first?.id || null);
      })
      .catch((e) => { if (!cancelled) { setKysy([]); setError(e.message); } });
    return () => { cancelled = true; };
  }, [instanceId, interviewId]);

  const remember = (p) => {
    setMirrorsByNode((prev) => ({ ...prev, [p.nodeId]: p }));
    setCurrent(p.id);
    onChange?.(p);
  };

  const start = (interview) => {
    setBusy(true); setError(null);
    call(API_ENDPOINTS.ME_PEILAUS(), { instanceId, interviewId: interview.id })
      .then(({ peilaus }) => remember(peilaus))
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  const stance = (p, index, value) => {
    setBusy(true); setError(null);
    call(API_ENDPOINTS.ME_PEILAUS_ACTION(p.id, 'stance'), { index, stance: value, note: notes[`${p.id}:${index}`] || '' })
      .then(({ peilaus }) => remember(peilaus))
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  if (kysy === null) return <Card><Muted>{t('me.loading')}</Muted></Card>;

  const open = Object.values(mirrorsByNode).find((m) => m.id === current) || null;
  const KIND = { pain: t('me.peilaus.kindPain'), cause: t('me.kipupiste.cause'), blocker: t('me.kipupiste.blocker') };

  return (
    <Card>
      {/* Pinned under a sub-card that already names the step: no repeat. */}
      {task.title && !interviewId && <Lens>{task.title}</Lens>}
      <Muted>{task.body || task.question || t('me.peilaus.intro')}</Muted>

      {/* Pinned to one pain point the card already names: no picker, only the
          start button until its mirror exists. */}
      {kysy.length > 0 && (!interviewId || !open) && (
        <div>
          {!interviewId && <Lens>{t('me.peilaus.pick')}</Lens>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.4rem' }}>
            {kysy.map((i) => {
              const existing = mirrorsByNode[i.nodeId];
              return (
                <Pick key={i.id} disabled={busy || (readOnly && !existing)}
                      onClick={() => (existing ? setCurrent(existing.id) : (readOnly ? null : start(i)))}>
                  <BookOpenCheck size={16} />
                  <span style={{ flex: 1 }}>{i.painPoint}</span>
                  <Decided>{existing ? (existing.status === 'done' ? t('me.preWorkDone') : t('me.kipupiste.inProgress')) : t('me.peilaus.start')}</Decided>
                </Pick>
              );
            })}
          </div>
        </div>
      )}
      {kysy.length === 0 && <Muted>{t('me.preWorkLocked')}</Muted>}

      {busy && !open && <Muted>{t('me.peilaus.loading')}</Muted>}
      {error && <ErrorLine>{error}</ErrorLine>}

      {open && open.mirrors.map((m, index) => (
        m.empty ? (
          <Muted key="empty">{t('me.peilaus.empty')}</Muted>
        ) : (
          <Mirror key={index} $decided={!!m.stance}>
            <You>
              <Lens>{t('me.peilaus.you')} · {KIND[m.itemKind] || m.itemKind}{m.itemLens ? ` · ${m.itemLens}` : ''}</Lens>
              <div>{m.itemText}</div>
            </You>
            <Source>
              <Lens>{t('me.peilaus.source')}</Lens>
              <div>{m.claim}</div>
              <Cite>{m.source}</Cite>
            </Source>
            <Question>{m.question}</Question>
            {m.stance ? (
              <Decided>{t(`me.peilaus.${m.stance}`)}{m.note ? ` — ${m.note}` : ''}</Decided>
            ) : readOnly ? (
              <Decided>{t('me.preWorkClosedShort')}</Decided>
            ) : (
              <>
                <Note value={notes[`${open.id}:${index}`] || ''} placeholder={t('me.peilaus.notePlaceholder')}
                      onChange={(e) => setNotes((prev) => ({ ...prev, [`${open.id}:${index}`]: e.target.value }))} disabled={busy} />
                <Actions>
                  <Stance $tone="holds" disabled={busy} onClick={() => stance(open, index, 'holds')}>{t('me.peilaus.holds')}</Stance>
                  <Stance $tone="not_here" disabled={busy} onClick={() => stance(open, index, 'not_here')}>{t('me.peilaus.not_here')}</Stance>
                  <Stance $tone="unsure" disabled={busy} onClick={() => stance(open, index, 'unsure')}>{t('me.peilaus.unsure')}</Stance>
                </Actions>
              </>
            )}
          </Mirror>
        )
      ))}
      {open && open.status === 'done' && !open.mirrors[0]?.empty && <Muted>{t('me.peilaus.done')}</Muted>}
    </Card>
  );
}
